/**
 * Admin → Blog — create/edit a post. Markdown body with live side-by-side
 * preview (react-markdown; the marketing site renders the same markdown with
 * remark at build time). Cover images upload to the marketing bucket under
 * blog/assets/ and are stored as root-relative paths.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import { ArrowLeft, Globe, ImagePlus, Loader2, Save, Undo2, X } from 'lucide-react';
import { apiClient } from '@/services/api-client';
import { formatApiError } from '@/lib/format-api-error';
import {
  blogKeys,
  formatBlogDate,
  resolveBlogImageUrl,
  toggleBlogPostStatus,
  uploadBlogAsset,
  type AdminBlogPost,
  type BlogConfig,
} from '@/services/blog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

interface PostFormData {
  slug: string;
  title: string;
  excerpt: string;
  author: string;
  content: string;
  featuredImage: string | null;
  imageAlt: string;
}

const emptyForm: PostFormData = {
  slug: '',
  title: '',
  excerpt: '',
  author: 'LegacyOrb Team',
  content: '',
  featuredImage: null,
  imageAlt: '',
};

function formFromPost(post: AdminBlogPost): PostFormData {
  return {
    slug: post.slug,
    title: post.title,
    excerpt: post.excerpt,
    author: post.author,
    content: post.content ?? '',
    featuredImage: post.featuredImage,
    imageAlt: post.imageAlt ?? '',
  };
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 120);
}

// Compact stand-in for the marketing site's `prose` styling, scoped to the
// preview pane via arbitrary-variant selectors (web has no typography plugin).
const previewProse =
  'text-sm leading-6 [&_h2]:mt-6 [&_h2]:text-xl [&_h2]:font-semibold [&_h3]:mt-4 [&_h3]:text-lg ' +
  '[&_h3]:font-semibold [&_p]:mt-3 [&_ul]:mt-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:mt-3 ' +
  '[&_ol]:list-decimal [&_ol]:pl-6 [&_li]:mt-1 [&_blockquote]:mt-4 [&_blockquote]:border-l-4 ' +
  '[&_blockquote]:border-orb-gold [&_blockquote]:pl-4 [&_blockquote]:italic ' +
  '[&_a]:text-orb-gold [&_a]:underline [&_strong]:font-semibold [&_img]:rounded-lg ' +
  '[&_code]:rounded [&_code]:bg-muted [&_code]:px-1';

export function BlogPostEditorPage() {
  const { id } = useParams<{ id: string }>();
  const isEditing = !!id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [form, setForm] = useState<PostFormData>(emptyForm);
  const [slugTouched, setSlugTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const set = <K extends keyof PostFormData>(key: K, value: PostFormData[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const { data: config } = useQuery({
    queryKey: blogKeys.config,
    queryFn: async () => {
      const response = await apiClient.get<BlogConfig>('/api/v1/admin/blog/config');
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
  });

  const { data: post, isLoading } = useQuery({
    queryKey: blogKeys.post(id ?? 'new'),
    enabled: isEditing,
    queryFn: async () => {
      const response = await apiClient.get<AdminBlogPost>(`/api/v1/admin/blog/posts/${id}`);
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
  });

  // Populate the form ONCE per post id — the detail query also refetches on
  // invalidation (e.g. after Publish/Unpublish), and re-running setForm then
  // would silently wipe any unsaved edits. Save syncs the form itself from
  // the PATCH response.
  const initializedPostId = useRef<string | null>(null);
  useEffect(() => {
    if (!post || initializedPostId.current === post.id) return;
    initializedPostId.current = post.id;
    setForm(formFromPost(post));
    setSlugTouched(true);
  }, [post]);

  const handleTitleChange = (title: string) => {
    setForm((f) => ({
      ...f,
      title,
      ...(!slugTouched && !isEditing && { slug: slugify(title) }),
    }));
  };

  const toPayload = () => ({
    slug: form.slug,
    title: form.title,
    excerpt: form.excerpt,
    author: form.author,
    content: form.content,
    featuredImage: form.featuredImage,
    imageAlt: form.imageAlt || null,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'blog'] });

  const handleSave = async () => {
    setError(null);
    setIsSaving(true);
    const response = isEditing
      ? await apiClient.patch<AdminBlogPost>(`/api/v1/admin/blog/posts/${id}`, toPayload())
      : await apiClient.post<AdminBlogPost>('/api/v1/admin/blog/posts', toPayload());
    setIsSaving(false);
    if (!response.success) {
      setError(formatApiError(response.error));
      return;
    }
    if (isEditing && response.data) {
      // Sync from the server's copy (it may normalize values) — this is the
      // one intentional overwrite of the form after initial load.
      setForm(formFromPost(response.data));
    }
    await invalidate();
    if (!isEditing && response.data) {
      navigate(`/dashboard/blog/${response.data.id}/edit`, { replace: true });
    }
  };

  const handleToggleStatus = async () => {
    if (!isEditing || !post) return;
    setError(null);
    setIsTogglingStatus(true);
    const response = await toggleBlogPostStatus(post);
    setIsTogglingStatus(false);
    if (!response.success) {
      setError(formatApiError(response.error));
      return;
    }
    await invalidate();
  };

  const handleUpload = async (file: File | undefined) => {
    if (!file) return;
    setError(null);
    setIsUploading(true);
    try {
      const asset = await uploadBlogAsset(file);
      setForm((f) => ({ ...f, featuredImage: asset.path }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed. Please try again.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const canSave = form.title.trim().length > 0 && form.slug.trim().length > 0 && !isSaving;
  const imagePreviewUrl = resolveBlogImageUrl(form.featuredImage, config?.assetsPublicBaseUrl);

  // Memoized so keystrokes in OTHER fields don't re-run the full markdown
  // parse — only content changes re-render the preview.
  const markdownPreview = useMemo(
    () => (form.content.trim() ? <ReactMarkdown>{form.content}</ReactMarkdown> : null),
    [form.content]
  );

  if (isEditing && isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/dashboard/blog">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="flex items-center gap-3 font-display text-2xl font-bold">
              {isEditing ? 'Edit post' : 'New post'}
              {post && (
                <Badge variant={post.status === 'published' ? 'default' : 'secondary'}>
                  {post.status}
                </Badge>
              )}
            </h1>
            {post?.publishedAt && (
              <p className="text-sm text-muted-foreground">
                Published {formatBlogDate(post.publishedAt, 'long')}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isEditing && post && (
            <Button variant="outline" onClick={handleToggleStatus} disabled={isTogglingStatus}>
              {isTogglingStatus ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : post.status === 'published' ? (
                <Undo2 className="mr-2 h-4 w-4" />
              ) : (
                <Globe className="mr-2 h-4 w-4" />
              )}
              {post.status === 'published' ? 'Unpublish' : 'Publish'}
            </Button>
          )}
          <Button onClick={handleSave} disabled={!canSave}>
            {isSaving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            {isEditing ? 'Save changes' : 'Create draft'}
          </Button>
        </div>
      </div>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <Card>
        <CardHeader>
          <CardTitle>Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={form.title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  placeholder="What if your life story could outlive you?"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="slug">Slug</Label>
                <Input
                  id="slug"
                  value={form.slug}
                  onChange={(e) => {
                    setSlugTouched(true);
                    set('slug', slugify(e.target.value) || e.target.value.toLowerCase());
                  }}
                  placeholder="what-if-your-life-story"
                />
                <p className="text-xs text-muted-foreground">
                  legacyorb.com/blog/{form.slug || '…'}{' '}
                  {isEditing && post?.status === 'published' && (
                    <span className="text-destructive">
                      — changing a live slug breaks existing links
                    </span>
                  )}
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="excerpt">Excerpt</Label>
                <Textarea
                  id="excerpt"
                  rows={3}
                  value={form.excerpt}
                  onChange={(e) => set('excerpt', e.target.value)}
                  placeholder="One or two sentences shown on the blog index card and in search results."
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="author">Author</Label>
                <Input
                  id="author"
                  value={form.author}
                  onChange={(e) => set('author', e.target.value)}
                />
              </div>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Cover image</Label>
                {imagePreviewUrl ? (
                  <div className="relative overflow-hidden rounded-lg border">
                    <img
                      src={imagePreviewUrl}
                      alt={form.imageAlt || 'Cover'}
                      className="aspect-video w-full object-cover"
                    />
                    <Button
                      variant="secondary"
                      size="sm"
                      className="absolute right-2 top-2"
                      onClick={() => set('featuredImage', null)}
                      title="Remove image"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex aspect-video w-full items-center justify-center rounded-lg border border-dashed bg-gradient-to-br from-orb-gold/10 to-orb-teal/10 text-sm text-muted-foreground">
                    No image — the card shows a gold/teal gradient
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  className="hidden"
                  onChange={(e) => handleUpload(e.target.files?.[0])}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading || config?.assetsConfigured === false}
                >
                  {isUploading ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <ImagePlus className="mr-2 h-4 w-4" />
                  )}
                  {form.featuredImage ? 'Replace image' : 'Upload image'}
                </Button>
                {config?.assetsConfigured === false && (
                  <p className="text-xs text-muted-foreground">
                    Uploads are disabled — BLOG_ASSETS_BUCKET is not configured on the API.
                  </p>
                )}
                <p className="text-xs text-muted-foreground">
                  Recommended 1600 × 900 (16:9), ≤ 8 MB. PNG, JPEG, WebP, or GIF.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="imageAlt">Image alt text</Label>
                <Input
                  id="imageAlt"
                  value={form.imageAlt}
                  onChange={(e) => set('imageAlt', e.target.value)}
                  placeholder="Describe the image for screen readers"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Content</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="content">Markdown</Label>
              <Textarea
                id="content"
                value={form.content}
                onChange={(e) => set('content', e.target.value)}
                rows={28}
                className="font-mono text-sm"
                placeholder={
                  'Write the post body in markdown.\n\n## Section heading\n\nUse `##` for sections (the title is the h1), `>` for pull-quotes, and **bold** for emphasis.'
                }
              />
            </div>
            <div className="space-y-2">
              <Label>Preview</Label>
              <div className="h-[42rem] overflow-y-auto rounded-md border bg-background p-6">
                {markdownPreview ? (
                  <div className={previewProse}>{markdownPreview}</div>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    The rendered post appears here as you type.
                  </p>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
